import React from "react";
import { Button, Chip, Link } from "@material-ui/core";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "../langs/useTranslation";
import { getScheduleTemplatesApi } from "@/apis/apiCollection";
import DataTable from "@/components/DataTable";
import HasPermission from "@/components/commons/HasPermission";
import ScheduleTaskType from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskType";
import ScheduleType from "@/containers/Schedule/CreateTemplate/TaskCreator/ScheduleType";
import TaskTime from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskTime";
import ScheduleTaskRange from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskRange";
import { utcFormatToLocalDateTimeWithSecond } from "./datetimeHandlers";

export const ScheduleTemplateListDatatable = React.forwardRef(
  (
    {
      className,
      style,
      getSearchParams,
      footer,
      children,
      onCheckAll,
      onLoaded,
      onView,
      onEdit,
      onDisable,
      onEnable,
      onDelete,
      onViewDevices
    },
    ref
  ) => {
    const { t } = useTranslation();

    const columns = [
      {
        name: "checkbox",
        data: row => {
          return `${row.schedule_template_id},${row.schedule_id}`;
        },
        type: "checkbox"
      },
      {
        name: "schedule_template_name",
        data: "schedule_template_name",
        title: t({ defaultMessage: "排程模板名称" }),
        searchable: true
      },
      {
        data: "task_type",
        title: t({ defaultMessage: "排程任务" }),
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleTaskType data={row} />, td);
        }
      },
      {
        data: "schedule_type",
        title: t({ defaultMessage: "循环模式" }),
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleType data={row} />, td);
        }
      },
      {
        data: "start_datetime",
        title: t({ defaultMessage: "执行时间" }),
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(<TaskTime data={row} />, td);
        }
      },
      {
        data: "end_datetime",
        title: t({ defaultMessage: "执行期间" }),
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleTaskRange data={row} />, td);
        }
      },
      {
        name: "schedule_template_name",
        data: "schedule_template_name",
        title: t({ defaultMessage: "模板状态" }),
        orderable: false,
        className: "center",
        createdCell: (td, data, row) => {
          ReactRender(
            <Chip
              size="small"
              color={row.status === 1 ? "primary" : "default"}
              label={
                row.status === 1
                  ? t({ defaultMessage: "启用" })
                  : t({ defaultMessage: "禁用" })
              }
            />,
            td
          );
        }
      },
      {
        name: "schedule_template_name",
        data: "group_names",
        title: `${t({ defaultMessage: "门店" })}`,
        orderable: false
      },
      {
        name: "schedule_template_name",
        data: "tags_names",
        title: t({ defaultMessage: "标签" }),
        orderable: false
      },
      {
        name: "schedule_template_name",
        data: "devices_count",
        title: t({ defaultMessage: "设备数" }),
        orderable: false,
        className: "center",
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              {data > 0 ? (
                <Link color="primary" onClick={() => onViewDevices(row)}>
                  {data}
                </Link>
              ) : (
                data
              )}
            </>,
            td
          );
        }
      },
      {
        name: "create_user_name",
        data: "create_user_name",
        title: t({ defaultMessage: "创建人" })
      },
      {
        name: "schedule_template_name",
        data: "schedule_template_created_at",
        title: t({ defaultMessage: "创建时间" }),
        searchable: false,
        render: data => {
          return utcFormatToLocalDateTimeWithSecond(data);
        }
      },
      {
        name: "schedule_template_name",
        data: "schedule_template_name",
        title: t({ defaultMessage: "操作" }),
        className: "sticky right operation",
        searchable: false,
        orderable: false,
        width: 220,
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              <Button size="small" color="primary" onClick={() => onView(row)}>
                {t({ defaultMessage: "查看" })}
              </Button>
              <HasPermission permission="schedule_task_policy_edit">
                <Button
                  size="small"
                  color="primary"
                  onClick={() => onEdit(row)}
                >
                  {t({ defaultMessage: "编辑" })}
                </Button>
              </HasPermission>
              <HasPermission permission="schedule_task_policy_enable">
                {row.status === 1 ? (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => onDisable(row)}
                  >
                    {t({ defaultMessage: "禁用" })}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => onEnable(row)}
                  >
                    {t({ defaultMessage: "启用" })}
                  </Button>
                )}
              </HasPermission>
              <HasPermission permission="schedule_task_policy_delete">
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => onDelete(row)}
                >
                  {t({ defaultMessage: "删除" })}
                </Button>
              </HasPermission>
            </>,
            td
          );
        }
      }
    ];

    const ajax = (data, callback) => {
      return getScheduleTemplatesApi({
        ...data,
        ...getSearchParams()
      }).then(res => {
        callback({
          data: res.data,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <DataTable
        ref={ref}
        ordering
        rowsGroup={["schedule_template_name:name", "create_user_name:name"]}
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        scrollable
        showCheckAll={false}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
      >
        {children}
      </DataTable>
    );
  }
);
